// AuthComponent.jsx
// Login/Signup UI with pencil aesthetic
//
// MODES:
// - 'login': Normal login form
// - 'signup': New account creation
// - 'forgot': Password reset request
// - 'complete-profile': Recovery mode - user is authenticated but needs to set up profile
//
// RECOVERY MODE (props.recoveryMode = true):
// This is used when a user clicked a password reset link but has NO existing profile.
// In this case, they're already authenticated (via the reset link) but need to:
// 1. Fill out their profile info (name, role, salary)
// 2. Set a new password
// This effectively completes their signup that was never finished.

import React, { useState } from 'react';
import { supabase, signUp, signIn, updateProfile, resetPassword, signInWithLinkedIn } from './supabaseClient';
import ResumeUpload from './ResumeUpload';

export default function AuthComponent({ onAuthSuccess, recoveryMode = false, recoveryUser = null }) {
  // If in recovery mode, start in 'complete-profile' mode
  const [mode, setMode] = useState(recoveryMode ? 'complete-profile' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [targetRole, setTargetRole] = useState('');
  const [targetSalaryMin, setTargetSalaryMin] = useState('');
  const [targetSalaryMax, setTargetSalaryMax] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [linkedInLoading, setLinkedInLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  // Resume upload flow state
  const [showResumeUpload, setShowResumeUpload] = useState(true); // Show upload first on signup
  const [parsedProfile, setParsedProfile] = useState(null); // Stores full parsed profile for saving
  const [title, setTitle] = useState(''); // Current/most recent job title

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    // COMPLETE-PROFILE MODE (Recovery flow for users without existing profile)
    // User is already authenticated via password reset link, just need to:
    // 1. Validate and set new password
    // 2. Create their profile
    if (mode === 'complete-profile' && recoveryUser) {
      // Validate passwords
      if (newPassword !== confirmPassword) {
        setError('Passwords do not match');
        setLoading(false);
        return;
      }
      if (newPassword.length < 6) {
        setError('Password must be at least 6 characters');
        setLoading(false);
        return;
      }

      try {
        // Update password
        const { error: passwordError } = await supabase.auth.updateUser({
          password: newPassword,
        });
        if (passwordError) throw passwordError;

        // Create profile
        const { error: profileError } = await updateProfile(recoveryUser.id, {
          name: name,
          email: recoveryUser.email,
          target_role: targetRole,
          target_salary_min: parseInt(targetSalaryMin) || 0,
          target_salary_max: parseInt(targetSalaryMax) || 0,
        });
        if (profileError) throw profileError;

        // Success - call the completion handler
        onAuthSuccess(recoveryUser);
      } catch (err) {
        console.error('Profile completion error:', err);
        setError(err.message || 'An error occurred. Please try again.');
      }
      setLoading(false);
      return;
    }

    // FORGOT PASSWORD MODE
    if (mode === 'forgot') {
      const { error } = await resetPassword(email);
      if (error) {
        setError(error.message);
      } else {
        setMessage('Password reset email sent! Check your inbox (and spam folder) for a link to reset your password.');
      }
    // SIGNUP MODE
    } else if (mode === 'signup') {
      const { data, error } = await signUp(email, password);
      if (error) {
        setError(error.message);
      } else if (data?.user) {
        // Check if this is a NEW signup or existing account
        // Supabase returns user.identities = [] for existing accounts (email already registered)
        // New signups have user.identities populated with the auth provider info
        const isNewUser = data.user?.identities?.length > 0;

        if (!isNewUser) {
          // Existing account - user needs to log in instead
          setError('An account with this email already exists. Please log in instead.');
          setLoading(false);
          return;
        }

        // Build profile data - include parsed resume data if available
        const profileData = {
          name: name,
          email: email,
          title: title || parsedProfile?.title || '',
          target_role: targetRole || parsedProfile?.title || '',
          target_salary_min: parseInt(targetSalaryMin) || 0,
          target_salary_max: parseInt(targetSalaryMax) || 0,
        };

        // Include rich profile data from resume if parsed
        if (parsedProfile) {
          profileData.summary = parsedProfile.summary || '';
          profileData.skills = parsedProfile.skills || [];
          profileData.experiences = parsedProfile.experiences || [];
          profileData.education = parsedProfile.education || [];
        }

        // Save profile data to localStorage in case email confirmation is required
        // App.jsx will check for this after user confirms and logs in
        localStorage.setItem('pendingProfileData', JSON.stringify({
          ...profileData,
          userId: data.user.id
        }));

        // Try to save profile now (will work if email confirmation is disabled)
        try {
          await updateProfile(data.user.id, profileData);
        } catch (profileError) {
          console.log('Profile will be saved after email confirmation');
        }

        setMessage('Check your email to confirm your account! Be sure to check your spam/junk folder if you don\'t see it within a few minutes.');
      }
    // LOGIN MODE
    } else {
      const { data, error } = await signIn(email, password);
      if (error) {
        setError(error.message);
      } else if (data?.user) {
        onAuthSuccess(data.user, data.session);
      }
    }
    setLoading(false);
  };

  const handleLinkedInSignIn = async () => {
    setLinkedInLoading(true);
    setError(null);
    try {
      const { error } = await signInWithLinkedIn();
      if (error) {
        setError(error.message);
        setLinkedInLoading(false);
      }
      // If successful, user will be redirected to LinkedIn
      // OAuth callback will be handled by App.jsx
    } catch (err) {
      setError('Failed to connect with LinkedIn. Please try again.');
      setLinkedInLoading(false);
    }
  };

  // Handle parsed profile from resume/LinkedIn PDF upload
  const handleProfileParsed = (profile, source) => {
    console.log('Profile parsed from', source, ':', profile);
    setParsedProfile(profile);
    setShowResumeUpload(false);

    // Pre-fill form fields
    if (profile.name) setName(profile.name);
    if (profile.title) {
      setTitle(profile.title);
      setTargetRole(profile.title); // Use current title as default target
    }
    if (profile.email) setEmail(profile.email);
  };

  // Skip resume upload
  const handleSkipResumeUpload = () => {
    setShowResumeUpload(false);
  };

  // RESUME UPLOAD SCREEN - Show first on signup
  if (mode === 'signup' && showResumeUpload && !recoveryMode) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#fffef9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: "'IBM Plex Sans', 'Inter', -apple-system, sans-serif",
      }}>
        <div style={{
          width: '100%',
          maxWidth: '480px',
          background: '#ffffff',
          border: '1px solid #e8e8e8',
          borderRadius: '3px',
          padding: '40px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
        }}>
          {/* Logo */}
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <svg width="52" height="52" viewBox="0 0 52 52" fill="none" style={{ margin: '0 auto 12px' }}>
              <path d="M8 38 Q16 28, 22 32 T34 26" stroke="#3d3d3d" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.8"/>
              <path d="M6 44 Q14 32, 20 38 T32 30" stroke="#3d3d3d" strokeWidth="1.5" fill="none" strokeLinecap="round" opacity="0.4"/>
              <circle cx="8" cy="38" r="2.5" fill="#d4736a"/>
              <g transform="rotate(-45 38 14)">
                <rect x="24" y="11" width="20" height="6" fill="#f7c948" stroke="#d4a832" strokeWidth="0.5"/>
                <rect x="44" y="11" width="4" height="6" fill="#b8b8b8"/>
                <rect x="48" y="11.5" width="3" height="5" rx="0.5" fill="#d4736a"/>
                <polygon points="24,11 24,17 20,14" fill="#c9a87c"/>
                <polygon points="20,14 22,12.5 22,15.5" fill="#3d3d3d"/>
              </g>
            </svg>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '700', fontFamily: "'Playfair Display', serif", color: '#3d3d3d' }}>
              Quick Start
            </h1>
            <p style={{ margin: '8px 0 0', fontSize: '14px', color: '#5a5a5a' }}>
              Upload your resume to auto-fill your profile
            </p>
          </div>

          <ResumeUpload
            onProfileParsed={handleProfileParsed}
            onSkip={handleSkipResumeUpload}
          />

          {/* Back to login link */}
          <div style={{ marginTop: '24px', textAlign: 'center', fontSize: '12px', color: '#7a7a7a' }}>
            Already have an account?{' '}
            <button
              onClick={() => { setMode('login'); setShowResumeUpload(true); }}
              style={{ background: 'none', border: 'none', color: '#8b6914', cursor: 'pointer', fontWeight: '600', padding: 0, fontSize: '12px' }}
            >
              Log in
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#fffef9',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: "'IBM Plex Sans', 'Inter', -apple-system, sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=Playfair+Display:wght@600;700&display=swap');
      `}</style>
      
      <div style={{
        width: '100%',
        maxWidth: '440px',
        background: '#ffffff',
        border: '1px solid #e8e8e8',
        borderRadius: '3px',
        padding: '40px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <svg width="60" height="60" viewBox="0 0 52 52" fill="none" style={{ margin: '0 auto 16px' }}>
            <path d="M8 38 Q16 28, 22 32 T34 26" stroke="#3d3d3d" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.8"/>
            <path d="M6 44 Q14 32, 20 38 T32 30" stroke="#3d3d3d" strokeWidth="1.5" fill="none" strokeLinecap="round" opacity="0.4"/>
            <circle cx="8" cy="38" r="2.5" fill="#d4736a"/>
            <g transform="rotate(-45 38 14)">
              <rect x="24" y="11" width="20" height="6" fill="#f7c948" stroke="#d4a832" strokeWidth="0.5"/>
              <rect x="44" y="11" width="4" height="6" fill="#b8b8b8"/>
              <rect x="48" y="11.5" width="3" height="5" rx="0.5" fill="#d4736a"/>
              <polygon points="24,11 24,17 20,14" fill="#c9a87c"/>
              <polygon points="20,14 22,12.5 22,15.5" fill="#3d3d3d"/>
            </g>
          </svg>
          <h1 style={{ 
            margin: 0, 
            fontSize: '28px', 
            fontWeight: '700',
            fontFamily: "'Playfair Display', serif",
            color: '#3d3d3d'
          }}>
            Manifold
          </h1>
          <p style={{ 
            margin: '4px 0 0', 
            fontSize: '11px', 
            color: '#7a7a7a',
            textTransform: 'uppercase',
            letterSpacing: '1.5px'
          }}>
            Career Architecture
          </p>
        </div>

        {/* Mode Toggle */}
        {mode === 'complete-profile' ? (
          /* COMPLETE PROFILE MODE - User came via password reset but has no profile */
          <div style={{
            marginBottom: '24px',
            paddingBottom: '16px',
            borderBottom: '2px solid #e8e8e8',
            textAlign: 'center',
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '600',
              color: '#3d3d3d',
              fontFamily: "'IBM Plex Sans', sans-serif",
            }}>
              Complete Your Profile
            </h2>
            <p style={{
              margin: '8px 0 0',
              fontSize: '13px',
              color: '#7a7a7a',
            }}>
              Set up your account to get started
            </p>
          </div>
        ) : mode === 'forgot' ? (
          <div style={{
            marginBottom: '24px',
            paddingBottom: '16px',
            borderBottom: '2px solid #e8e8e8',
            textAlign: 'center',
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '600',
              color: '#3d3d3d',
              fontFamily: "'IBM Plex Sans', sans-serif",
            }}>
              Reset Password
            </h2>
            <p style={{
              margin: '8px 0 0',
              fontSize: '13px',
              color: '#7a7a7a',
            }}>
              Enter your email and we'll send you a reset link
            </p>
          </div>
        ) : (
          <div style={{
            display: 'flex',
            marginBottom: '24px',
            borderBottom: '2px solid #e8e8e8',
          }}>
            <button
              onClick={() => setMode('login')}
              style={{
                flex: 1,
                padding: '12px',
                background: 'none',
                border: 'none',
                borderBottom: mode === 'login' ? '2px solid #f7c948' : '2px solid transparent',
                marginBottom: '-2px',
                color: mode === 'login' ? '#3d3d3d' : '#7a7a7a',
                fontWeight: mode === 'login' ? '600' : '500',
                fontSize: '14px',
                cursor: 'pointer',
                fontFamily: "'IBM Plex Sans', sans-serif",
              }}
            >
              Log In
            </button>
            <button
              onClick={() => setMode('signup')}
              style={{
                flex: 1,
                padding: '12px',
                background: 'none',
                border: 'none',
                borderBottom: mode === 'signup' ? '2px solid #f7c948' : '2px solid transparent',
                marginBottom: '-2px',
                color: mode === 'signup' ? '#3d3d3d' : '#7a7a7a',
                fontWeight: mode === 'signup' ? '600' : '500',
                fontSize: '14px',
                cursor: 'pointer',
                fontFamily: "'IBM Plex Sans', sans-serif",
              }}
            >
              Sign Up
            </button>
          </div>
        )}

        {/*
          LinkedIn Sign-In Button - HIDDEN FOR v1.0 RELEASE

          Reason: LinkedIn OIDC only provides basic info (name, email, headline).
          Full work history requires LinkedIn Partner Program approval (enterprise process).
          Keeping code for future implementation once Partner Program access is granted.

          To re-enable: Remove the false && condition below.
          See GitHub Issue #109 for LinkedIn profile import via copy/paste workaround.
        */}
        {false && (mode === 'login' || mode === 'signup') && (
          <>
            <button
              type="button"
              onClick={handleLinkedInSignIn}
              disabled={linkedInLoading}
              style={{
                width: '100%',
                padding: '12px 24px',
                background: linkedInLoading ? '#e8e8e8' : '#ffffff',
                border: '1px solid #0a66c2',
                borderRadius: '3px',
                color: linkedInLoading ? '#7a7a7a' : '#0a66c2',
                fontSize: '14px',
                fontWeight: '600',
                cursor: linkedInLoading ? 'not-allowed' : 'pointer',
                fontFamily: "'IBM Plex Sans', sans-serif",
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                marginBottom: '20px',
              }}
            >
              {/* LinkedIn Logo */}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
              {linkedInLoading ? 'Connecting...' : 'Continue with LinkedIn'}
            </button>

            {/* Divider */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '20px',
              gap: '12px',
            }}>
              <div style={{ flex: 1, height: '1px', background: '#e8e8e8' }} />
              <span style={{ fontSize: '11px', color: '#7a7a7a', textTransform: 'uppercase', letterSpacing: '1px' }}>
                or with email
              </span>
              <div style={{ flex: 1, height: '1px', background: '#e8e8e8' }} />
            </div>
          </>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* COMPLETE-PROFILE MODE - For users who clicked reset link but have no profile */}
          {mode === 'complete-profile' && recoveryUser && (
            <>
              {/* Info box */}
              <div style={{
                background: '#f8f6f0',
                border: '1px solid #e8e4d9',
                borderRadius: '3px',
                padding: '12px 16px',
                marginBottom: '20px',
                fontSize: '12px',
                color: '#5a5a5a',
              }}>
                <strong style={{ color: '#3d3d3d' }}>Almost there!</strong><br />
                Fill out your profile and set a password to complete your account.
              </div>

              {/* Email (read-only) */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', color: '#7a7a7a', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '500' }}>
                  Email
                </label>
                <input
                  type="email"
                  value={recoveryUser.email}
                  disabled
                  style={{
                    width: '100%', padding: '12px 16px', background: '#f5f5f5', border: '1px solid #d8d8d8', borderRadius: '3px', fontSize: '14px', color: '#7a7a7a', fontFamily: "'IBM Plex Sans', sans-serif", boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* Name */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', color: '#7a7a7a', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '500' }}>
                  Full Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  style={{
                    width: '100%', padding: '12px 16px', background: '#ffffff', border: '1px solid #b8b8b8', borderRadius: '3px', fontSize: '14px', color: '#3d3d3d', fontFamily: "'IBM Plex Sans', sans-serif", boxSizing: 'border-box',
                  }}
                  placeholder="Jane Smith"
                />
              </div>

              {/* Target Role */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', color: '#7a7a7a', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '500' }}>
                  Target Role
                </label>
                <input
                  type="text"
                  value={targetRole}
                  onChange={(e) => setTargetRole(e.target.value)}
                  style={{
                    width: '100%', padding: '12px 16px', background: '#ffffff', border: '1px solid #b8b8b8', borderRadius: '3px', fontSize: '14px', color: '#3d3d3d', fontFamily: "'IBM Plex Sans', sans-serif", boxSizing: 'border-box',
                  }}
                  placeholder="e.g., Product Engineer, Sales Manager"
                />
                <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {['Product Engineer', 'Software Engineer', 'Product Manager', 'Sales', 'Operations'].map(role => (
                    <button key={role} type="button" onClick={() => setTargetRole(role)} style={{ background: targetRole === role ? '#f7c948' : '#f5f5f5', border: '1px solid #e5e5e5', borderRadius: '3px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer', color: '#3d3d3d' }}>
                      {role}
                    </button>
                  ))}
                </div>
              </div>

              {/* Salary Range */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '12px', color: '#7a7a7a', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '500' }}>
                  Target Salary Range
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <input type="number" value={targetSalaryMin} onChange={(e) => setTargetSalaryMin(e.target.value)} style={{ width: '100%', padding: '12px 16px', background: '#ffffff', border: '1px solid #b8b8b8', borderRadius: '3px', fontSize: '14px', color: '#3d3d3d', fontFamily: "'IBM Plex Sans', sans-serif", boxSizing: 'border-box' }} placeholder="Min (e.g., 150000)" />
                  <input type="number" value={targetSalaryMax} onChange={(e) => setTargetSalaryMax(e.target.value)} style={{ width: '100%', padding: '12px 16px', background: '#ffffff', border: '1px solid #b8b8b8', borderRadius: '3px', fontSize: '14px', color: '#3d3d3d', fontFamily: "'IBM Plex Sans', sans-serif", boxSizing: 'border-box' }} placeholder="Max (e.g., 200000)" />
                </div>
              </div>

              {/* Divider */}
              <div style={{ borderTop: '1px solid #e8e8e8', margin: '20px 0', position: 'relative' }}>
                <span style={{ position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)', background: '#fff', padding: '0 12px', fontSize: '11px', color: '#7a7a7a', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Set Password
                </span>
              </div>

              {/* New Password */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', color: '#7a7a7a', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '500' }}>
                  Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  style={{
                    width: '100%', padding: '12px 16px', background: '#ffffff', border: '1px solid #b8b8b8', borderRadius: '3px', fontSize: '14px', color: '#3d3d3d', fontFamily: "'IBM Plex Sans', sans-serif", boxSizing: 'border-box',
                  }}
                  placeholder="At least 6 characters"
                />
              </div>

              {/* Confirm Password */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '12px', color: '#7a7a7a', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '500' }}>
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  style={{
                    width: '100%', padding: '12px 16px', background: '#ffffff', border: '1px solid #b8b8b8', borderRadius: '3px', fontSize: '14px', color: '#3d3d3d', fontFamily: "'IBM Plex Sans', sans-serif", boxSizing: 'border-box',
                  }}
                  placeholder="Re-enter your password"
                />
              </div>
            </>
          )}

          {/* Signup-only fields */}
          {mode === 'signup' && (
            <>
              {/* Profile Imported Banner */}
              {parsedProfile && (
                <div style={{
                  marginBottom: '16px',
                  padding: '12px 14px',
                  background: '#e8f5e9',
                  border: '1px solid #81c784',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" fill="#4caf50"/>
                    <path d="M8 12l3 3 5-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <div>
                    <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#2e7d32' }}>
                      Profile imported successfully!
                    </p>
                    <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#388e3c' }}>
                      {parsedProfile.experiences?.length || 0} work experiences, {parsedProfile.skills?.length || 0} skills found
                    </p>
                  </div>
                </div>
              )}

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  color: '#7a7a7a',
                  marginBottom: '6px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: '500'
                }}>
                  Full Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: '#ffffff',
                    border: '1px solid #b8b8b8',
                    borderRadius: '3px',
                    fontSize: '14px',
                    color: '#3d3d3d',
                    fontFamily: "'IBM Plex Sans', sans-serif",
                    boxSizing: 'border-box',
                  }}
                  placeholder="Jane Smith"
                />
              </div>
            </>
          )}

          {/* Email field - hidden in complete-profile mode (has its own email display) */}
          {mode !== 'complete-profile' && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '12px',
              color: '#7a7a7a',
              marginBottom: '6px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontWeight: '500'
            }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '12px 16px',
                background: '#ffffff',
                border: '1px solid #b8b8b8',
                borderRadius: '3px',
                fontSize: '14px',
                color: '#3d3d3d',
                fontFamily: "'IBM Plex Sans', sans-serif",
                boxSizing: 'border-box',
              }}
              placeholder="you@example.com"
            />
          </div>
          )}

          {/* Password field - hidden in complete-profile and forgot modes */}
          {mode !== 'forgot' && mode !== 'complete-profile' && (
            <div style={{ marginBottom: mode === 'signup' ? '16px' : '12px' }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                color: '#7a7a7a',
                marginBottom: '6px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: '500'
              }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: '#ffffff',
                  border: '1px solid #b8b8b8',
                  borderRadius: '3px',
                  fontSize: '14px',
                  color: '#3d3d3d',
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  boxSizing: 'border-box',
                }}
                placeholder="••••••••"
              />
            </div>
          )}

          {mode === 'login' && (
            <div style={{ marginBottom: '24px', textAlign: 'right' }}>
              <button
                type="button"
                onClick={() => setMode('forgot')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#8b6914',
                  cursor: 'pointer',
                  fontWeight: '500',
                  padding: 0,
                  fontSize: '12px',
                  fontFamily: "'IBM Plex Sans', sans-serif",
                }}
              >
                Forgot password?
              </button>
            </div>
          )}

          {mode === 'forgot' && (
            <div style={{ marginBottom: '24px' }} />
          )}

          {/* Signup-only fields continued */}
          {mode === 'signup' && (
            <>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '12px', 
                  color: '#7a7a7a', 
                  marginBottom: '6px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: '500'
                }}>
                  Target Role
                </label>
                <input
                  type="text"
                  value={targetRole}
                  onChange={(e) => setTargetRole(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: '#ffffff',
                    border: '1px solid #b8b8b8',
                    borderRadius: '3px',
                    fontSize: '14px',
                    color: '#3d3d3d',
                    fontFamily: "'IBM Plex Sans', sans-serif",
                    boxSizing: 'border-box',
                  }}
                  placeholder="e.g., Product Engineer, Sales Manager"
                />
                <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {['Product Engineer', 'Software Engineer', 'Product Manager', 'Sales', 'Operations'].map(role => (
                    <button
                      key={role}
                      type="button"
                      onClick={() => setTargetRole(role)}
                      style={{
                        background: targetRole === role ? '#f7c948' : '#f5f5f5',
                        border: '1px solid #e5e5e5',
                        borderRadius: '3px',
                        padding: '4px 10px',
                        fontSize: '11px',
                        cursor: 'pointer',
                        color: '#3d3d3d'
                      }}
                    >
                      {role}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '12px', 
                  color: '#7a7a7a', 
                  marginBottom: '6px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: '500'
                }}>
                  Target Salary Range
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <input
                    type="number"
                    value={targetSalaryMin}
                    onChange={(e) => setTargetSalaryMin(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: '#ffffff',
                      border: '1px solid #b8b8b8',
                      borderRadius: '3px',
                      fontSize: '14px',
                      color: '#3d3d3d',
                      fontFamily: "'IBM Plex Sans', sans-serif",
                      boxSizing: 'border-box',
                    }}
                    placeholder="Min (e.g., 150000)"
                  />
                  <input
                    type="number"
                    value={targetSalaryMax}
                    onChange={(e) => setTargetSalaryMax(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: '#ffffff',
                      border: '1px solid #b8b8b8',
                      borderRadius: '3px',
                      fontSize: '14px',
                      color: '#3d3d3d',
                      fontFamily: "'IBM Plex Sans', sans-serif",
                      boxSizing: 'border-box',
                    }}
                    placeholder="Max (e.g., 200000)"
                  />
                </div>
              </div>
            </>
          )}

          {error && (
            <div style={{
              padding: '12px 16px',
              background: '#fce8e8',
              border: '1px solid #d4736a',
              borderRadius: '3px',
              color: '#943c33',
              fontSize: '13px',
              marginBottom: '16px',
            }}>
              {error}
            </div>
          )}

          {message && (
            <div style={{
              padding: '12px 16px',
              background: '#e0efe0',
              border: '1px solid #5a8f5a',
              borderRadius: '3px',
              color: '#3d6b3d',
              fontSize: '13px',
              marginBottom: '16px',
            }}>
              {message}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px 24px',
              background: loading ? '#fdf4d7' : '#f7c948',
              border: '2px solid #d4a832',
              borderRadius: '3px',
              color: loading ? '#9a9a9a' : '#3d3d3d',
              fontSize: '14px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: "'IBM Plex Sans', sans-serif",
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {loading ? 'Please wait...' : mode === 'complete-profile' ? 'Complete Setup' : mode === 'login' ? 'Log In' : mode === 'forgot' ? 'Send Reset Link' : 'Create Account'}
          </button>
        </form>

        {/* Footer - Hidden in complete-profile mode */}
        {mode !== 'complete-profile' && (
        <div style={{
          marginTop: '32px',
          paddingTop: '24px',
          borderTop: '1px solid #e8e8e8',
          textAlign: 'center',
          fontSize: '12px',
          color: '#7a7a7a'
        }}>
          {mode === 'forgot' ? (
            <span>Remember your password? <button onClick={() => setMode('login')} style={{ background: 'none', border: 'none', color: '#8b6914', cursor: 'pointer', fontWeight: '600', padding: 0, fontSize: '12px' }}>Back to login</button></span>
          ) : mode === 'login' ? (
            <span>Don't have an account? <button onClick={() => setMode('signup')} style={{ background: 'none', border: 'none', color: '#8b6914', cursor: 'pointer', fontWeight: '600', padding: 0, fontSize: '12px' }}>Sign up</button></span>
          ) : (
            <span>Already have an account? <button onClick={() => setMode('login')} style={{ background: 'none', border: 'none', color: '#8b6914', cursor: 'pointer', fontWeight: '600', padding: 0, fontSize: '12px' }}>Log in</button></span>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
